import React, { useEffect, useState } from 'react';
import { TableRow, Grid } from '@material-ui/core';
import TableComponents from 'components/Table';
import RecommendedPolicy from './RecommendedPolicy';
import {
  sumFunds,
  sumFundInitial,
  sumTotalOngoing,
  formatCurrency,
  formatPercent,
  unFormatCurrency
} from 'utils';
import { BoxWrapper, Button, Modal } from 'components/Base';
import PlatformHoldings from './PlatformHoldings/PlatformHoldings';
import Store from 'store/store';
import { PlatformType } from 'store/modules/Platform/types';
import api from 'api';
import Router from 'components/Router/Router';
import { Notify } from 'utils';
import SellectionOverride from './SellectionOverride/SellectionOverride';

const { useParams } = Router;
const { connect, selectors, actions } = Store;

interface RecommendedPoliciesProps {
  fields: any;
  platforms: any;
  holdings: any;
  setHoldings: (data: any) => void;
  portfolioRiskScore: any;
  caseInfo: any;
}

const RecommendedPolicies = ({
  fields,
  platforms,
  holdings,
  setHoldings,
  portfolioRiskScore,
  caseInfo
}: RecommendedPoliciesProps) => {
  const isEvalue = caseInfo.riskProfiler === 'EValue';
  const riskHeaders = isEvalue
    ? ['Risk']
    : ['Growth assets', 'Risk comfort score'];
  const [riskScore, setRiskScore] = useState('' as any);
  const [isModalPlatformHoldingOpen, setIsModalPlatfromHolding] =
    useState(false);
  const [isSellectionOverrideOpen, setIsSellectionOverride] = useState(false);
  const policies = fields.getAll();

  useEffect(() => {
    const portfolioRiskProfile = portfolioRiskScore.find(
      (score: any) => score.name === 'Recommended'
    );

    if (portfolioRiskProfile) {
      setRiskScore(portfolioRiskProfile.risk_score);
    }
  }, [portfolioRiskScore]);

  const sumTotalOngoingCharges = sumTotalOngoing(policies);
  const fundsSum = sumFunds(policies);
  const fundsSumInitial = sumFundInitial(policies);
  const { portfolio } = useParams() as any;

  const totalOngoingCharge = sumTotalOngoingCharges / fundsSum;

  const initialValues = platforms.map((platformDetails: PlatformType) => {
    const findExHolding = holdings.filter(
      (holding: any) => holding.platform === platformDetails.platform
    );

    const isHoldingExist = findExHolding.length > 0;

    if (isHoldingExist) {
      const { family_linked_amount, id, personal_amount, platform } =
        findExHolding[0];

      return {
        id: id,
        name: platformDetails.platform_label,
        family_linked: platformDetails.family_linked,
        personal_amount: personal_amount && formatCurrency(personal_amount),
        family_linked_amount:
          family_linked_amount && formatCurrency(family_linked_amount),
        platform: platform,
        isChecked: !!family_linked_amount || !!personal_amount
      };
    }
    return {
      id: null,
      name: platformDetails.platform_label,
      platform: platformDetails.platform,
      family_linked: platformDetails.family_linked,
      personal_amount: formatCurrency(0),
      family_linked_amount: formatCurrency(0),
      isChecked: false
    };
  });
  const submitHoldings = (val: any) => {
    const apiArray: any = [];

    val.holdings.forEach((platformDetails: any) => {
      const data = {
        id: platformDetails.id,
        platform: platformDetails.platform,
        personal_amount: unFormatCurrency(platformDetails.personal_amount),
        family_linked: platformDetails.family_linked,
        family_linked_amount: unFormatCurrency(
          platformDetails.family_linked_amount
        )
      };
      if (platformDetails.id) {
        if (!platformDetails.isChecked) {
          apiArray.push(
            api.deleteHolding({ portfolio, id: platformDetails.id })
          );
          return;
        }
        apiArray.push(
          api.updateHolding({ portfolio, id: platformDetails.id, data: data })
        );
      } else {
        if (platformDetails.isChecked) {
          apiArray.push(api.setHoldings({ portfolio, data: data }));
        }
      }

      return apiArray;
    });

    return Promise.all(apiArray)
      .then(() => {
        return Promise.all([
          api.getHoldings({ portfolio }),
          api.evaluate({ portfolio })
        ]).then(([holdings]: any) => {
          setHoldings(holdings.data);
          setIsModalPlatfromHolding(false);
        });
      })
      .catch(err => {
        if (err.response.data.message) {
          Notify.error(err.response.data.message);
        }
      });
  };
  return (
    <BoxWrapper
      title="Recommended portfolio"
      headerChildren={
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button onClick={() => setIsSellectionOverride(true)}>
            Selection override
          </Button>
          <Button
            onClick={() => setIsModalPlatfromHolding(true)}
            style={{ marginLeft: '1rem' }}
          >
            Existing platform holdings
          </Button>
        </Grid>
      }
    >
      <Modal
        isOpen={isModalPlatformHoldingOpen}
        closeModalFunc={() => setIsModalPlatfromHolding(false)}
      >
        <PlatformHoldings
          closeModal={() => setIsModalPlatfromHolding(false)}
          initialValues={{ holdings: initialValues }}
          onSubmit={submitHoldings}
        />
      </Modal>
      <Modal
        isOpen={isSellectionOverrideOpen}
        closeModalFunc={() => setIsSellectionOverride(false)}
      >
        <SellectionOverride closeModal={() => setIsSellectionOverride(false)} />
      </Modal>
      {policies.length > 0 && (
        <Grid container>
          <TableComponents.Table>
            <TableComponents.TableHead
              cells={[
                '',
                'Policy owner(s)',
                'Policy number',
                'Platform',
                'Policy provider',
                'Policy type',
                'Fund value',
                'Annualised contributions',
                'Annualised withdrawals',
                'Total ongoing charge',
                ...riskHeaders,
                'Id'
              ]}
            />
            <TableComponents.TableBody>
              {fields.map((field: any, index: number) => {
                const policyValues = fields.get(index);

                return (
                  <RecommendedPolicy
                    policyValues={policyValues}
                    field={field}
                    key={index}
                    isEvalue={isEvalue}
                  />
                );
              })}
            </TableComponents.TableBody>
            <TableComponents.TableFooter>
              <TableRow>
                <TableComponents.TableCell />
                <TableComponents.TableCell />
                <TableComponents.TableCell />
                <TableComponents.TableCell />
                <TableComponents.TableCell />

                <TableComponents.TableCell>Overall</TableComponents.TableCell>
                <TableComponents.TableCell>
                  {formatCurrency(fundsSumInitial)}
                </TableComponents.TableCell>
                <TableComponents.TableCell />
                <TableComponents.TableCell />
                <TableComponents.TableCell>
                  {formatPercent(
                    totalOngoingCharge ? totalOngoingCharge * 100 : 0
                  )}
                </TableComponents.TableCell>
                {isEvalue ? (
                  <TableComponents.TableCell>
                    {riskScore || '-'}
                  </TableComponents.TableCell>
                ) : (
                  <>
                    <TableComponents.TableCell>
                      {formatPercent(riskScore.growth_score)}
                    </TableComponents.TableCell>
                    <TableComponents.TableCell>
                      {`${riskScore.range?.low} - ${riskScore.range?.high}`}
                    </TableComponents.TableCell>
                  </>
                )}
                <TableComponents.TableCell />
              </TableRow>
            </TableComponents.TableFooter>
          </TableComponents.Table>
        </Grid>
      )}
    </BoxWrapper>
  );
};

export default connect(
  (state: any) => ({
    platforms: selectors.caseSelectors.providers(state),
    holdings: selectors.caseSelectors.holdings(state),
    portfolioRiskScore: selectors.caseSelectors.portfolioRiskScore(state),
    caseInfo: selectors.caseSelectors.case(state)
  }),
  {
    setHoldings: actions.caseActions.setHoldings
  }
)(RecommendedPolicies);
